import React, { useEffect, useState } from 'react';

interface AiTaskProgressOverlayProps {
  title: string;
  etaLabel: string;
  progressPercent: number;
  steps: string[];
  stepIndex: number;
}

const AiTaskProgressOverlay: React.FC<AiTaskProgressOverlayProps> = ({
  title,
  etaLabel,
  progressPercent,
  steps,
  stepIndex,
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const safeSteps = Array.isArray(steps) && steps.length > 0 ? steps : ['处理中'];
  const currentStep = safeSteps[Math.max(0, Math.min(stepIndex, safeSteps.length - 1))];
  const clamped = Math.max(8, Math.min(99, progressPercent));
  const elapsedLabel = `${(elapsedMs / 1000).toFixed(1)}秒`;

  useEffect(() => {
    const startedAt = Date.now();
    const tick = () => setElapsedMs(Date.now() - startedAt);
    tick();
    const timer = window.setInterval(tick, 100);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/45 backdrop-blur-[3px] flex items-center justify-center p-4">
      <div className="relative w-full max-w-[360px] overflow-hidden rounded-2xl border border-[#dcc49a] bg-gradient-to-br from-[#fffaf0] via-[#f8efdc] to-[#f0dfbe] px-5 py-[18px] shadow-[0_30px_80px_-45px_rgba(41,27,9,0.7)]">
        <div className="absolute -top-10 -right-9 h-28 w-28 rounded-full bg-[#f5d38c]/45 blur-2xl" />
        <div className="absolute -bottom-10 -left-9 h-28 w-28 rounded-full bg-[#ecd1a0]/45 blur-2xl" />

        <div className="relative flex items-center gap-3">
          <div className="relative h-14 w-14 shrink-0">
            <span className="absolute inset-0 rounded-full border-[2.5px] border-[#8b6b3d]/20" />
            <span className="absolute inset-[4px] rounded-full border-[2.5px] border-[#8b6b3d]/35 animate-spin [animation-duration:1.7s]" />
            <span className="absolute inset-[9px] rounded-full border-[2.5px] border-transparent border-t-[#8b6b3d] border-r-[#b2884a] animate-spin [animation-duration:1.05s]" />
            <span className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8b6b3d] shadow-[0_0_12px_rgba(139,107,61,0.55)] animate-pulse" />
          </div>
          <div>
            <p className="text-[17px] font-bold leading-none text-[#4e3518] [font-family:'STSong','Songti_SC','SimSun',serif]">
              {title}
            </p>
            <p className="mt-2 text-xs text-[#6b5432]">已耗时 {elapsedLabel}</p>
            <p className="mt-1 text-[11px] text-[#8a7148]">预计总耗时约 {etaLabel}</p>
          </div>
        </div>

        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[#ead9b8]">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#8b6b3d] via-[#bb944f] to-[#8b6b3d] transition-all duration-100 ease-out"
            style={{ width: `${clamped}%` }}
          />
        </div>

        <div className="mt-3 rounded-lg border border-[#d9c29c] bg-[#f7ecd9] px-3 py-2">
          <p className="text-[11px] text-[#7a623e]">当前任务</p>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-[#5a3e1d]">
            <span className="h-2 w-2 rounded-full border border-[#8b6b3d] bg-[#d4b079] animate-pulse" />
            <span>{currentStep}</span>
            <span className="ml-auto text-[11px] text-[#8a7148]">{Math.round(progressPercent)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiTaskProgressOverlay;
